import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

/**
 * Diálogo de confirmación imperativo, compartido por toda la app.
 *
 * Reemplaza `window.confirm()` nativo (que en iPad/Safari a veces no aparece y
 * rompe el flujo, además de verse fuera del tema). Uso:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: '¿Eliminar?', message: '…', destructive: true }))) return;
 *
 * Un único `<ConfirmProvider>` montado en `ThemedProvider` sirve a toda la app
 * (mismo criterio que el Toaster global). No instanciar diálogos ad-hoc por vista.
 */
export interface ConfirmOptions {
  /** Título del diálogo. Opcional. */
  title?: string;
  /** Cuerpo. Los saltos de línea (`\n`) de un string se respetan. */
  message: ReactNode;
  /** Texto del botón de confirmar. Default "Confirmar". */
  confirmLabel?: string;
  /** Texto del botón de cancelar. Default "Cancelar". */
  cancelLabel?: string;
  /** Acción irreversible → botón de confirmar en rojo. */
  destructive?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Hook: devuelve `confirm(opts) => Promise<boolean>`. */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm debe usarse dentro de <ConfirmProvider>');
  return ctx;
}

const useStyles = makeStyles({
  message: { whiteSpace: 'pre-wrap', color: tokens.colorNeutralForeground1 },
  danger: {
    backgroundColor: tokens.colorStatusDangerBackground3,
    color: tokens.colorNeutralForegroundOnBrand,
    ':hover': { backgroundColor: tokens.colorStatusDangerBackground3Hover },
    ':hover:active': { backgroundColor: tokens.colorStatusDangerBackground3Pressed },
  },
});

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const styles = useStyles();
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => {
    setOpts(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const close = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOpts(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={opts !== null}
        onOpenChange={(_, d) => {
          if (!d.open) close(false);
        }}
      >
        <DialogSurface>
          <DialogBody>
            {opts?.title && <DialogTitle>{opts.title}</DialogTitle>}
            <DialogContent>
              <div className={styles.message}>{opts?.message}</div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => close(false)}>
                {opts?.cancelLabel ?? 'Cancelar'}
              </Button>
              <Button
                appearance="primary"
                className={opts?.destructive ? styles.danger : undefined}
                onClick={() => close(true)}
              >
                {opts?.confirmLabel ?? 'Confirmar'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
